import { authorizedFetch } from "./base";
import {
  Portfolio,
  PortfolioPayload,
  RawPortfolioResponse,
  SyncProgressResponse,
} from "./types";
import { parseDecimal } from "./utils";

function parsePortfolio(p: RawPortfolioResponse): Portfolio {
  return {
    id: p.id,
    name: p.name,
    brokerage: p.brokerage ?? null,
    base_currency: p.base_currency,
    total_value: parseDecimal(p.total_value) ?? 0,
    invested_capital: parseDecimal(p.invested_capital) ?? 0,
    unrealized_gain_base_total: parseDecimal(p.unrealized_gain_base_total) ?? 0,
    realized_sales_gain_base_total:
      parseDecimal(p.realized_sales_gain_base_total) ?? 0,
    dividend_base_total: parseDecimal(p.dividend_base_total) ?? 0,
    interest_base_total: parseDecimal(p.interest_base_total) ?? 0,
    realized_gain_total: parseDecimal(p.realized_gain_total) ?? 0,
    fx_pnl_base_total: parseDecimal(p.fx_pnl_base_total) ?? 0,
    total_gain_loss: parseDecimal(p.total_gain_loss) ?? 0,
    total_gain_loss_percent: parseDecimal(p.total_gain_loss_percent) ?? 0,
    holdings_value: parseDecimal(p.holdings_value) ?? 0,
    holdings_gain_loss: parseDecimal(p.holdings_gain_loss) ?? 0,
    holdings_gain_loss_percent: parseDecimal(p.holdings_gain_loss_percent) ?? 0,
    cash_total: parseDecimal(p.cash_total) ?? 0,
    cash_balances: p.cash_balances,
    irr_inception: parseDecimal(p.irr_inception) ?? null,
    twr_inception: parseDecimal(p.twr_inception) ?? null,
    performance_refresh_pending: Boolean(p.performance_refresh_pending),
    holdings_count: p.holdings_count ?? 0,
    largest_position_ticker: p.largest_position_ticker ?? null,
    largest_position_pct: parseDecimal(p.largest_position_pct) ?? null,
    is_combined: p.is_combined ?? false,
    track_cash: p.track_cash ?? true,
    include_in_rollup: p.include_in_rollup ?? true,
    has_api_key: p.has_api_key ?? false,
    custom_asset_class: p.custom_asset_class ?? null,
    logo_key: p.logo_key ?? null,
    card_metrics: p.card_metrics ?? null,
    display_order: p.display_order ?? 0,
    last_synced_at: p.last_synced_at ?? null,
    is_locked: p.is_locked ?? false,
    is_demo: p.is_demo ?? false,
  };
}

export async function getPortfolios(): Promise<Portfolio[]> {
  const rawData = await authorizedFetch<RawPortfolioResponse[]>(`/portfolios/`);
  return Array.isArray(rawData) ? rawData.map(parsePortfolio) : [];
}

export async function createPortfolio(
  portfolioData: PortfolioPayload,
): Promise<Portfolio> {
  const raw = await authorizedFetch<RawPortfolioResponse>(`/portfolios/`, {
    method: "POST",
    body: JSON.stringify(portfolioData),
  });
  return parsePortfolio(raw);
}

export async function createDemoPortfolio(): Promise<Portfolio> {
  const raw = await authorizedFetch<RawPortfolioResponse>(`/portfolios/demo`, {
    method: "POST",
  });
  return parsePortfolio(raw);
}

export async function deletePortfolio(portfolioId: number) {
  return authorizedFetch(`/portfolios/${portfolioId}`, {
    method: "DELETE",
  });
}

export async function updatePortfolio(
  portfolioId: number,
  name?: string,
  api_key?: string,
  // undefined leaves the logo alone, a string sets it, null clears it.
  logoKey?: string | null,
  // undefined leaves the card metrics alone, an array replaces the selection.
  cardMetrics?: string[],
): Promise<Portfolio> {
  const payload: {
    name?: string;
    api_key?: string;
    logo_key?: string;
    clear_logo?: boolean;
    card_metrics?: string[];
  } = {};
  if (name) payload.name = name;
  if (api_key) payload.api_key = api_key;
  if (typeof logoKey === "string") payload.logo_key = logoKey;
  else if (logoKey === null) payload.clear_logo = true;
  if (cardMetrics) payload.card_metrics = cardMetrics;
  const raw = await authorizedFetch<RawPortfolioResponse>(
    `/portfolios/${portfolioId}`,
    {
      method: "PUT",
      body: JSON.stringify(payload),
    },
  );
  return parsePortfolio(raw);
}

export async function reorderPortfolios(
  portfolioIds: number[],
): Promise<Portfolio[]> {
  const raw = await authorizedFetch<RawPortfolioResponse[]>(
    `/portfolios/reorder`,
    {
      method: "PUT",
      body: JSON.stringify({ portfolio_ids: portfolioIds }),
    },
  );
  return Array.isArray(raw) ? raw.map(parsePortfolio) : [];
}

export async function togglePortfolioRollupInclusion(
  portfolioId: number,
  include_in_rollup: boolean,
  skipRecalc = false,
): Promise<Portfolio> {
  const url = skipRecalc
    ? `/portfolios/${portfolioId}?skip_recalc=true`
    : `/portfolios/${portfolioId}`;
  const raw = await authorizedFetch<RawPortfolioResponse>(url, {
    method: "PUT",
    body: JSON.stringify({ include_in_rollup }),
  });
  return parsePortfolio(raw);
}

export async function clearPortfolioApiKey(
  portfolioId: number,
): Promise<Portfolio> {
  const raw = await authorizedFetch<RawPortfolioResponse>(
    `/portfolios/${portfolioId}`,
    {
      method: "PUT",
      body: JSON.stringify({ clear_api_key: true }),
    },
  );
  return parsePortfolio(raw);
}

export async function recalculatePortfolio(portfolioId: number) {
  return authorizedFetch(`/portfolios/${portfolioId}/recalculate`, {
    method: "POST",
  });
}

export async function recalculateRollup(
  rollupPortfolioId: number,
): Promise<void> {
  await authorizedFetch(`/portfolios/${rollupPortfolioId}/recalculate-rollup`, {
    method: "POST",
  });
}

export async function getRecalculationProgress(
  portfolioId: number,
): Promise<SyncProgressResponse> {
  return authorizedFetch<SyncProgressResponse>(
    `/portfolios/${portfolioId}/recalculation-progress`,
  );
}
